import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES, DISPLAY_MOMENT_DATE_TIME_HM, DISPLAY_MOMENT_DATE_YMD, FORUM, NEWS_CATEGORY, NEWS_STATUS, ROUTES, TOPIC_STATUS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { NEWS_STATUS_TYPE, TOPIC_STATUS_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { BanIcon, Check, CircleCheckIcon, EllipsisIcon, PlusIcon, SearchIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewsTableItem } from '@/types/news';
import Calendar from '@/components/calendar/Calendar';
import { Label } from '@/components/common/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Link, useNavigate } from 'react-router-dom';

export default observer(function NewsList() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const { newsStore: { keepSearchParams, totalByStatus, paging, newsList, totalRecord, defaultPaging, getAllNews, setObservable },
        modalStore: { showModal }
    } = useStore();

    // state
    const [searchState, setSearchState] = useState<Object>();

    // validate schema
    const validateSchema = yup.object().shape({
        title: yup.string(),
        categoryIds: yup.array().of(yup.number()),
        fromCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        toCreatedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        fromPublishedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        toPublishedDate: yup
            .lazy((value) => {
                if (value === '') {
                    return yup.string().oneOf(['']);
                } else {
                    return yup.date();
                }
            }),
        status: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            title: '',
            categoryIds: [],
            fromCreatedDate: '',
            toCreatedDate: '',
            fromPublishedDate: '',
            toPublishedDate: '',
            status: 'all'
        }
    });

    // lifecycle
    useEffect(() => {
        form.reset();
        onFetch();

        return () => {
            setObservable('paging', defaultPaging);
        };
    }, []);

    // functions
    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        const paramsToSend = {
            ...searchParams,

            status: searchParams?.status === 'all' ? '' : searchParams?.status,
            createDateStart: searchParams?.fromCreatedDate ? formatDateTime(searchParams?.fromCreatedDate, DISPLAY_MOMENT_DATE_YMD) : '',
            createDateEnd: searchParams?.toCreatedDate ? formatDateTime(searchParams?.toCreatedDate, DISPLAY_MOMENT_DATE_YMD) : '',
            publishedDateStart: searchParams?.fromPublishedDate ? formatDateTime(searchParams?.fromPublishedDate, DISPLAY_MOMENT_DATE_YMD) : '',
            publishedDateEnd: searchParams?.toPublishedDate ? formatDateTime(searchParams?.toPublishedDate, DISPLAY_MOMENT_DATE_YMD) : ''
        };

        getAllNews(paramsToSend, paging);
    };

    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        setObservable('keepSearchParams', toJS(searchParams));
        onFetch(searchParams, defaultPaging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleClickNewNews = () => {
        navigate(ROUTES.newsCreate.href);
    };

    // columns
    const columns: ColumnDef<NewsTableItem>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            header: t('words_title.title'),
            size: 180,

            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    <p className='line-clamp-2'>{original.title}</p>
                </div>
            )
        },
        {
            header: t('words_title.category'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{t(`words_title.${NEWS_CATEGORY[original.categoryId].key}`)}</div>
            )
        },
        {
            header: t('words_title.hot_topic'),
            size: 70,

            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center'>{original.isHotTopic ? <Check size={28} color='#020817' /> : ''}</div>
            )
        },
        {
            header: t('words_title.total_reads'),
            size: 80,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='f'>{original.totalRead}</div>
            )
        },
        {
            header: t('words_title.published_date'),
            size: 160,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(original.publishedDate, DISPLAY_MOMENT_DATE_YMD)}</div>
            )
        },
        {
            header: t('words_title.created_date'),
            size: 160,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(original.createdAt, DISPLAY_MOMENT_DATE_TIME_HM)}</div>
            )
        },

        {
            header: t('words_title.created_by'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {
                        original?.creatorName
                    }
                </div>
            )
        },

        {
            header: t('words_title.status'),
            size: 130,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex items-center justify-start gap-2'>
                    <div className={classNames('w-2 h-2 rounded-full flex-none', original.status === NEWS_STATUS_TYPE.Published ? 'bg-status-active' : 'bg-status-inactive')}></div>
                    <div>{t(`words_title.${NEWS_STATUS[original.status].key}`)}</div>
                </div>
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown
                        status={original.status}
                        code={original.code}
                        onFetch={onFetch}
                    />
                </div>
            )
        }
    ];

    return (
        <div className='pb-4'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>
                            <div>
                                <FormField
                                    control={form.control}
                                    name='title'
                                    render={({ field }) => (
                                        <FormItem className='flex gap-2 items-center'>
                                            <FormLabel className='w-1/5'>{t('words_title.title')}</FormLabel>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div>
                                <FormField
                                    control={form.control}
                                    name='categoryIds'

                                    render={({ field }) => (
                                        <FormItem className='flex gap-2 items-start'>
                                            {' '}
                                            {/* items-start để label trên cùng với checkbox */}
                                            <FormLabel className='w-1/5'>{t('words_title.category')}</FormLabel>
                                            <FormControl className='w-4/5'>
                                                <div className='flex flex-wrap gap-4'>
                                                    {' '}
                                                    {/* Thêm flex-wrap và giảm gap nếu muốn */}
                                                    {Object.values(NEWS_CATEGORY).map(({ key, value }) => (
                                                        <div key={key} className='flex items-center gap-2'>
                                                            <Checkbox
                                                                id={`${key}Checkbox`}
                                                                checked={field.value?.includes(value)} // ✅ BIND checked state
                                                                onCheckedChange={(checked) => {
                                                                    if (checked) {
                                                                        field.onChange([...(field.value || []), value]);
                                                                    } else {
                                                                        field.onChange(field.value?.filter(v => v !== value));
                                                                    }
                                                                }}
                                                            />
                                                            <label className='cursor-pointer' htmlFor={`${key}Checkbox`}>
                                                                {t(`words_title.${key}`)}
                                                            </label>
                                                        </div>
                                                    ))}

                                                </div>
                                            </FormControl>
                                        </FormItem>

                                    )}
                                />
                            </div>
                            <div className='flex items-start'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.promotion_created_date')}</Label>
                                <div className='flex items-center gap-3 w-4/5 ml-2'>
                                    <FormField
                                        control={form.control}
                                        name='fromCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}

                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>

                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='toCreatedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}

                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className='flex items-start'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.published_date')}</Label>
                                <div className='flex items-center gap-3 w-4/5 ml-2'>
                                    <FormField
                                        control={form.control}
                                        name='fromPublishedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}

                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='toPublishedDate'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}

                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.status')}</Label>
                                            <Select

                                                key={field.value}
                                                onValueChange={field.onChange}
                                                {...field}
                                                className='w-4/5  ml-2'
                                            >

                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('placeholder.choose')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value='all'
                                                    >
                                                        <span>{t('words_title.all')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={NEWS_STATUS_TYPE.Published.toString()}
                                                    >
                                                        <span>{t('words_title.published')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={NEWS_STATUS_TYPE.NotPublished.toString()}
                                                    >
                                                        <span>{t('words_title.not_published')}</span>
                                                    </SelectItem>

                                                </SelectContent>
                                            </Select>
                                        </div>

                                    </FormItem>
                                )}
                            />

                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <div className='bg-white mt-4 rounded p-4'>
                <div>
                    <Button
                        onClick={handleClickNewNews}
                        variant='default'
                        className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto'
                    >
                        <PlusIcon className='w-7 h-7 text-white' />
                        <span>{t('button.add_new_article')}</span>
                    </Button>
                </div>

                <Table
                    columns={columns}
                    data={newsList}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    state={paging}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                />

            </div>

        </div>
    );
});

type ActionMenuDropdownProps = {
    status: number,
    code: string,
    onFetch: Function
};

const ActionMenuDropdown = ({ status, code, onFetch }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        newsStore: { updateNewsStatus, deleteNews, keepSearchParams, paging },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    const handleClickDelete = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_delete_topic'),
            onSave: async () => {
                const res = await flowResult(deleteNews(code));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.delete_success'));
                }
            }
        }));
    };

    const handleClickEdit = () => {
        navigate(`${code}`);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>

                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={SquarePenIcon} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};
